import { useState, useEffect, useCallback } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { HelpTip } from '../../components/HelpTip';
import type { EstadoComisionesMl } from './types';

/** Comisión real de ML: estado del refresco y botón (sólo admin). Sólo LEE de ML. */
export function ComisionesMlPanel({ esAdmin }: { esAdmin: boolean }) {
  const toast = useToast();
  const [estado, setEstado] = useState<EstadoComisionesMl | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);
  // Cuenta cada consulta terminada (bien o con error): re-arma el temporizador aunque el estado no cambie.
  const [consultas, setConsultas] = useState(0);

  const cargar = useCallback(() => {
    api.get<EstadoComisionesMl>('/api/v3/precios/comisiones-ml')
      .then(e => { setEstado(e); setError(null); })
      .catch(e => setError(extractErrorMessage(e)))
      .finally(() => setConsultas(n => n + 1));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras el último estado conocido diga "en curso", refresca cada 3 s; un error no corta el sondeo
  // (se reintenta con el mismo intervalo). Para cuando termina la corrida o al desmontar el panel.
  useEffect(() => {
    if (!estado?.enCurso) return;
    const t = setTimeout(cargar, 3000);
    return () => clearTimeout(t);
  }, [estado, consultas, cargar]);

  const actualizar = async () => {
    setIniciando(true);
    try {
      const r = await api.post<{ iniciado: boolean; estado: EstadoComisionesMl }>('/api/v3/precios/comisiones-ml/actualizar', {});
      setEstado(r.estado);
      toast.addToast(r.iniciado ? 'Actualizando comisiones de ML (sólo lectura)…' : 'Ya hay una actualización en curso', 'info');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setIniciando(false);
    }
  };

  const r = estado?.reporte;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Comisiones de ML</h2>
        <HelpTip tips={[
          'Comisión real de MercadoLibre por categoría y tipo de publicación (porcentaje + cargo fijo por unidad), consultada a ML sólo en modo lectura.',
          estado
            ? `Se usa si tiene ${estado.vigenciaDias} días o menos; si no, la tabla de arriba y el precio muestra "comisión estimada".`
            : 'Se usa mientras esté vigente; si no, la tabla de arriba y el precio muestra "comisión estimada".',
          'El cargo fijo depende del tramo de precio: si el precio sugerido cae en otro tramo se avisa "cargo fijo puede variar".',
          estado?.horario ?? 'Se actualiza sola todos los días.',
        ]} />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!estado && !error && <p className="text-sm text-slate-400">Cargando…</p>}
      {estado && (
        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <span>Última actualización: <strong>{estado.ultimaCorrida ? new Date(estado.ultimaCorrida).toLocaleString('es-AR') : 'nunca'}</strong>
              {r && ` (${r.origen === 'cron' ? 'automática' : 'manual'})`}</span>
            <span>Comisiones vigentes: <strong className="font-mono">{estado.vigentes}</strong></span>
            {estado.enCurso && <Badge tone="amber">actualizando…</Badge>}
            {estado.ok === false && <Badge tone="red">con error</Badge>}
          </div>
          {r && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>Consultas a ML: <span className="font-mono">{r.consultas}</span></span>
              <span>Guardadas: <span className="font-mono">{r.guardadas}</span></span>
              <span>Errores: <span className="font-mono">{r.errores}</span></span>
              <span>Precios cubiertos: <span className="font-mono">{r.cubiertas}</span></span>
              <span>Pendientes: <span className="font-mono">{r.pendientes}</span></span>
              <span>Sin categoría: <span className="font-mono">{r.sinCategoria}</span></span>
              {r.cortadoPorLimite && <span className="text-amber-700">ML pidió esperar (429): sigue en la próxima corrida</span>}
            </div>
          )}
          {r?.error && <p className="text-xs text-red-600">{r.error}</p>}
          {esAdmin && (
            <div className="flex justify-end">
              <Button onClick={actualizar} loading={iniciando} disabled={estado.enCurso}>Actualizar comisiones de ML</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
